<script setup lang="ts">
// @file Visually hidden skip-style button that moves keyboard focus into the
// tldraw canvas. Lets keyboard-only users reach canvas shapes without having to
// click. Tabbing past it without activating advances to the next focusable
// element in the tab order (e.g. the toolbar's Interact button).
import { __ } from '@@/bits/intl'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import { withTldrawEditor } from '@@/vuecomposables/useTldrawEditor'

// Mirrors tldraw's own hidden `SkipToMainContent` button (we disable tldraw's
// default UI via `hideUi`, so we need to provide an equivalent ourselves):
// select the first shape in reading order, scroll it into view if it's
// offscreen, then focus the container on the next tick so tldraw enters
// shape-navigation mode — without the delay tldraw can flip into editing mode.
const onClick = (event: MouseEvent): void => {
  event.preventDefault()
  ;(event.currentTarget as HTMLElement | null)?.blur()

  withTldrawEditor((editor) => {
    const shapes = editor.getCurrentPageShapesInReadingOrder()
    if (shapes.length > 0) {
      editor.setSelectedShapes([shapes[0].id])
    }
    // The 100ms pause matters: if we focus the canvas immediately after
    // selecting the shape, tldraw enters shape-edit mode instead of
    // shape-navigation mode. We use editor.timers.setTimeout (not the global
    // one) so the pending focus is dropped automatically if the editor
    // unmounts before the 100ms is up.
    editor.timers.setTimeout(() => editor.getContainer().focus(), 100)
  })
}
</script>

<template>
  <OzContainedButton
    data-testid="moveFocusToWhiteboardCanvasButton"
    :class="[
      // Hidden until keyboard-focused, then revealed at the top-center of the
      // canvas. Avoids `not-sr-only` because it adds unwanted layout styles.
      '[&:not(:focus-visible)]:sr-only',
      'absolute',
      'z-edit-post',
      'top-4',
      'start-1/2',
      '-translate-s-1/2',
    ]"
    :color-scheme="OzContainedButtonColorScheme.Primary"
    :size-preset="OzContainedButtonSizePreset.H32px"
    :text="__('Move focus to the card content')"
    @click="onClick"
  />
</template>
